'use client';

import { c as _c } from "react/compiler-runtime";
import NextImage, { getImageProps, type ImageProps } from 'next/image';
import { preload } from 'react-dom';
import { buildShopifyImageUrl, isShopifyImageUrl } from '@/lib/shopify-image-url';

/**
 * `next/image` loader that delegates resizing to the Shopify CDN.
 *
 * When the Shopify wrapper hands an image to `<NextImage>` with this loader
 * active, Next.js stops generating `/_next/image?url=...` URLs and instead
 * calls this function once per breakpoint width to produce a CDN URL.
 * Result: Shopify pays the resize cost, not Vercel.
 */
function shopifyLoader({
  src,
  width,
  quality
}: {
  src: string;
  width: number;
  quality?: number;
}): string {
  return buildShopifyImageUrl(src, {
    width,
    quality
  });
}

/** Extensions that should bypass any resize/optimizer (Shopify or Next). */
const NEVER_OPTIMIZE_EXTENSIONS = new Set(['svg', 'gif']);
function srcShouldSkipOptimization(src: unknown): boolean {
  if (typeof src !== 'string') return false;
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }
  const lastSegment = url.pathname.split('/').pop() ?? '';
  const dot = lastSegment.lastIndexOf('.');
  if (dot < 0 || dot === lastSegment.length - 1) return false;
  return NEVER_OPTIMIZE_EXTENSIONS.has(lastSegment.slice(dot + 1).toLowerCase());
}

/** Keep imperative image warm-ups on the same CDN and srcset as rendered images. */
export function getShopifyImageProps({
  src,
  ...props
}: ImageProps) {
  const skipOptimization = srcShouldSkipOptimization(src);
  return getImageProps({
    ...props,
    src,
    loader: typeof src === 'string' && isShopifyImageUrl(src) && !skipOptimization ? shopifyLoader : undefined,
    unoptimized: props.unoptimized ?? skipOptimization
  }).props;
}
export interface ShopifyImageProps extends ImageProps {
  /**
   * Accepted for backward compatibility with existing call sites; the
   * wrapper no longer renders any blur placeholder so this is a no-op.
   */
  disableBlur?: boolean;
  /**
   * Forwarded to Next/Image so LCP images are discoverable from the initial
   * document instead of waiting for CSS/JS discovery.
   */
  preload?: boolean;
}

/**
 * Image wrapper that routes Shopify-hosted assets through Shopify's CDN
 * resize service while leaving every non-Shopify URL on Next/Vercel image
 * optimization. No blur placeholder is rendered.
 */
export function ShopifyImage(t0) {
  const $ = _c(18);
  let draggable;
  let preload;
  let priority;
  let props;
  let src;
  let t1;
  let unoptimized;
  if ($[0] !== t0) {
    const {
      src: t2,
      className: t3,
      disableBlur: _disableBlur,
      draggable: t4,
      preload: t5,
      priority: t6,
      unoptimized: t7,
      blurDataURL: _blurDataURL,
      placeholder: _placeholder,
      ...t8
    } = t0;
    src = t2;
    t1 = t3;
    draggable = t4;
    preload = t5;
    priority = t6;
    unoptimized = t7;
    props = t8;
    $[0] = t0;
    $[1] = draggable;
    $[2] = preload;
    $[3] = priority;
    $[4] = props;
    $[5] = src;
    $[6] = t1;
    $[7] = unoptimized;
  } else {
    draggable = $[1];
    preload = $[2];
    priority = $[3];
    props = $[4];
    src = $[5];
    t1 = $[6];
    unoptimized = $[7];
  }
  const className = t1 === undefined ? "" : t1;
  const isShopify = typeof src === "string" && isShopifyImageUrl(src);
  let t2;
  if ($[8] !== src) {
    t2 = srcShouldSkipOptimization(src);
    $[8] = src;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const skipOptimization = t2;
  const shouldPreload = preload ?? priority;
  const t3 = draggable ?? false;
  const t4 = isShopify && !skipOptimization ? shopifyLoader : undefined;
  const t5 = unoptimized ?? skipOptimization;
  let t6;
  if ($[10] !== className || $[11] !== props || $[12] !== shouldPreload || $[13] !== src || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <NextImage src={src} {...props} className={className} draggable={t3} placeholder="empty" preload={shouldPreload} loader={t4} unoptimized={t5} />;
    $[10] = className;
    $[11] = props;
    $[12] = shouldPreload;
    $[13] = src;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
export interface ArtDirectedPreloadVariant {
  src: string;
  /** The `sizes` the matching <img> uses. */
  sizes: string;
  /** Media query for the viewport that actually displays this variant. */
  media: string;
}

/**
 * Preload hints for art-directed images (a desktop rendition and a mobile
 * rendition of the same slot, toggled with `hidden md:block` / `md:hidden`).
 *
 * Preloads ignore CSS, so with `<Image preload>` on both variants phones
 * downloaded the desktop rendition and desktops the mobile one (36 KB wasted
 * on the PDP gallery alone). React's server renderer also auto-preloads every
 * `<img fetchPriority="high">`, keyed by srcset + sizes, and drops any later
 * `<link rel="preload">` with the same key — so the only way to scope the hint
 * is to register it FIRST through `ReactDOM.preload()`, whose options are
 * spread verbatim onto the emitted `<link>` (that is how `media` gets through;
 * it is not part of the typed options). Render this above the two images and
 * leave `preload` off them; keep their `loading="eager"` / `fetchPriority`.
 * Server-only on purpose: the client-side `preload()` strips `media`, and on a
 * client navigation the eager <img> is rendered immediately anyway.
 */
export function ArtDirectedImagePreload({
  variants
}: {
  variants: ArtDirectedPreloadVariant[];
}) {
  if (typeof window !== 'undefined') return null;
  for (const variant of variants) {
    const isShopify = isShopifyImageUrl(variant.src);
    const skipOptimization = srcShouldSkipOptimization(variant.src);
    const {
      props
    } = getImageProps({
      src: variant.src,
      alt: '',
      fill: true,
      sizes: variant.sizes,
      loader: isShopify && !skipOptimization ? shopifyLoader : undefined,
      unoptimized: skipOptimization
    });
    preload(props.src, {
      as: 'image',
      imageSrcSet: props.srcSet,
      imageSizes: props.sizes,
      fetchPriority: 'high',
      media: variant.media
    } as Parameters<typeof preload>[1]);
  }
  return null;
}